import React from 'react';

import styled from 'styled-components';

import { useSidebarMenuLogic } from 'components/layout/hooks';
import { CommunityActivitySummary } from 'types/recommendations';

import ActionPlanStatusColumn from './ActionPlanStatusColumn';
import RecentAlertsColumn from './RecentAlertsColumn';
import SalesTalkTracksColumn from './SalesTalkTracksColumn';

const Container = styled.div`
  border-radius: 10px;
  border: solid 1px var(--line-gray);
  padding: 30px;
  padding-bottom: 0;
  background-color: var(--white);
`;

const Header = styled.div`
  display: flex;
  flex-flow: row;
  gap: 10px;
  padding-bottom: 20px;
  border-bottom: 1px solid var(--line-gray);
`;

const ImageContainer = styled.div`
  background-image: linear-gradient(to bottom, var(--lavender-frost), var(--lavender-frost-end));
  border-radius: 4px;
  height: 32px;
  width: 32px;
`;

const Title = styled.div`
  font-size: 20px;
  font-family: var(--font-bold);
`;

const Subtitle = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
`;

const Columns = styled.div`
  display: flex;
  flex-flow: row;

  & > * + * {
    border-left: solid 1px var(--line-gray);
  }
`;

type Props = {
  data?: CommunityActivitySummary;
  communityId?: number;
};

const ActivitySummaryCard = ({ data, communityId }: Props) => {
  const { showAlerts, showActionPlans, showSalesTalkTracks } = useSidebarMenuLogic();

  const alerts = data?.alerts?.recent ?? [];
  const alertsTotalCount = data?.alerts?.total_count ?? 0;

  const categories = data?.sales_talk_tracks?.categories ?? [];
  const salesTalkTracksTotalCount = data?.sales_talk_tracks?.total_count ?? 0;

  if (!showAlerts && !showActionPlans && !showSalesTalkTracks) {
    return null;
  }

  return (
    <Container data-test-id="activity-summary-card">
      <Header>
        <ImageContainer>
          <img src="/icons/notifications-blue.svg" width="32" height="32" alt="" />
        </ImageContainer>
        <div>
          <Title>Alerts, Action Plans & Sales Talk Tracks</Title>
          <Subtitle>
            Ongoing monitoring, separate from the weekly digest · every alert becomes a plan you can assign to your
            team.
          </Subtitle>
        </div>
      </Header>
      <Columns>
        {showAlerts && <RecentAlertsColumn alerts={alerts} totalCount={alertsTotalCount} communityId={communityId} />}
        {showActionPlans && (
          <ActionPlanStatusColumn
            actionPlans={data?.action_plans}
            actionPlansPath={`/community/${communityId}/action-plans`}
          />
        )}
        {showSalesTalkTracks && (
          <SalesTalkTracksColumn categories={categories} totalCount={salesTalkTracksTotalCount} />
        )}
      </Columns>
    </Container>
  );
};

export default ActivitySummaryCard;
